import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import { Close, Download } from "@mui/icons-material";

/**
 * Reusable ViewDialog component for read-only data display
 * 
 * @param {boolean} open - Dialog open state
 * @param {string} title - Dialog title
 * @param {Function} onClose - Close handler
 * @param {Array} fields - Array of field definitions: [{ label, value, span }]
 * @param {ReactNode} children - Custom content (alternative to fields)
 * @param {string} downloadLabel - Download button label
 * @param {Function} onDownload - Download handler
 * @param {number} maxWidth - Dialog max width
 * @param {boolean} fullWidth - Full width dialog
 * @param {Object} sx - Custom styles
 */
const ViewDialog = ({
  open,
  title,
  onClose,
  fields = [],
  children,
  downloadLabel,
  onDownload,
  maxWidth = "sm",
  fullWidth = true,
  sx = {},
}) => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (open) setHidden(false);
  }, [open]);

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setHidden(true);
    onClose?.();
  };

  return (
    <Dialog
      open={open && !hidden}
      onClose={(e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose(e);
      }}
      TransitionProps={{ timeout: 0 }}
      disableRestoreFocus
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      BackdropProps={{ sx: { ...(hidden ? { opacity: 0 } : {}) } }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          ...(hidden ? { opacity: 0, visibility: "hidden" } : {}),
          ...sx,
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {title}
          <IconButton
            onClick={(e) => handleClose(e)}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {children || (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {fields.map((field, index) => (
              <Grid item xs={12} sm={field.span || 6} key={index}>
                <Typography variant="caption" color="text.secondary">
                  {field.label}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {typeof field.value === "string" || typeof field.value === "number" ? (
                    <Typography variant="body2">{field.value || "N/A"}</Typography>
                  ) : (
                    field.value
                  )}
                </Box>
                {index < fields.length - 1 && (
                  <Divider sx={{ mt: 2, display: { xs: "block", sm: "none" } }} />
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onDownload && (
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={onDownload}
          >
            {downloadLabel || "Download"}
          </Button>
        )}
        <Button onClick={(e) => handleClose(e)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDialog;

